/**
 * Mantenimiento: capturado a mano (Req. 22.2, 22.3), propuesto por fórmula
 * (Req. 22.1; 0.5.0) y estimado con los días registrados (Req. 22.4; 0.7.0).
 *
 * Es un historial: los registros no se borran ni se reescriben. Regla
 * acordada para las metas: un valor nuevo aplica desde hoy o una fecha
 * futura, y los días anteriores conservan el que tenían. Para corregir, se
 * captura el valor correcto desde hoy; con la misma fecha cuenta el más
 * reciente.
 */

import { db, leerSettings } from './schema';
import { enEscritura } from './tx';
import type { MacrosDiarios, Maintenance, MealEntry } from './types';
import { ahoraIso, esFechaLocal, sumarDias, type FechaLocal } from '@/domain/dates';
import { ErrorValidacion } from '@/domain/errors';
import {
  DIAS_VENTANA,
  estimarMantenimiento,
  kcalDeMacros,
  macrosCuadran,
  propuestaPorFormula,
  type DatosDeEstimacion,
  type DatosDeFormula,
  type DiaDeEstimacion,
  type ResultadoEstimado,
  type ResultadoFormula,
} from '@/domain/mantenimiento';
import { formatearKcal, sumar } from '@/domain/nutricion';
import { pesajeLegible, pesosPorDia, resumenDePeso, tendenciaDe } from '@/domain/tendencia';
import type { UnidadLongitud, UnidadPeso } from '@/domain/units';

/** Del más reciente al más antiguo. LECTURA PURA. */
export async function leerMantenimientos(): Promise<Maintenance[]> {
  const registros = await db.maintenance.toArray();
  return registros.sort((a, b) => b.desde.localeCompare(a.desde) || b.creadoEn.localeCompare(a.creadoEn));
}

export interface PropuestaDeHoy {
  resultado: ResultadoFormula;
  /** Para presentar el paso a paso en las unidades de la persona. */
  unidades: { peso: UnidadPeso; longitud: UnidadLongitud };
}

/**
 * La propuesta por fórmula con los datos de hoy (Req. 22.1). El peso es la
 * tendencia de 7 días o, sin tendencia, el último pesaje. LECTURA PURA.
 */
export async function leerPropuestaPorFormula(hoy: FechaLocal): Promise<PropuestaDeHoy> {
  const [ajustes, pesajes] = await Promise.all([leerSettings(), db.weighIns.toArray()]);
  const resumen = resumenDePeso(pesajes.filter(pesajeLegible), ajustes.basculaTendenciaId, hoy);
  const pesoKg = resumen.tendenciaKg ?? resumen.ultimo?.pesoKg ?? null;
  const datos: DatosDeFormula = {
    pesoKg,
    pesoDe: pesoKg === null ? null : resumen.tendenciaKg !== null ? 'tendencia' : 'ultimo',
    estaturaCm: ajustes.estaturaCm,
    fechaNacimiento: ajustes.fechaNacimiento,
    sexo: ajustes.sexo,
    nivelActividad: ajustes.nivelActividad,
  };
  const unidades = { peso: ajustes.unidadPeso, longitud: ajustes.unidadLongitud };
  return { resultado: propuestaPorFormula(datos, hoy, unidades), unidades };
}

export interface EstimadoDeHoy {
  resultado: ResultadoEstimado;
  /** Los días que se miraron: del más antiguo al más reciente. */
  dias: DiaDeEstimacion[];
  /** El primer día de la ventana (hoy − 21). */
  desde: FechaLocal;
  /** El último (ayer): hoy todavía no termina. */
  hasta: FechaLocal;
}

/**
 * El mantenimiento estimado con lo registrado (Req. 22.4; design.md, 8.7):
 * el promedio de kcal de los días con registro completo de los 21 días
 * anteriores, menos el cambio de la tendencia de peso entre los extremos de
 * esa ventana. Hoy no entra: todavía no termina.
 *
 * Si faltan días o tendencia, el resultado lo dice y no estima nada. Solo
 * lee: la decisión se guarda aparte, con `guardarDecisionDePropuesta`.
 * LECTURA PURA.
 */
export async function leerEstimadoDeMantenimiento(hoy: FechaLocal): Promise<EstimadoDeHoy> {
  if (!esFechaLocal(hoy)) throw new ErrorValidacion('La fecha no es válida.');
  const hasta = sumarDias(hoy, -1);
  const desde = sumarDias(hoy, -DIAS_VENTANA);
  const [ajustes, entradas, marcados, pesajes] = await Promise.all([
    leerSettings(),
    db.mealEntries.where('fecha').between(desde, hasta, true, true).toArray(),
    db.days.where('fecha').between(desde, hasta, true, true).toArray(),
    db.weighIns.toArray(),
  ]);

  const incompletos = new Set(marcados.filter((d) => d.registroIncompleto).map((d) => d.fecha));
  const porDia = new Map<FechaLocal, MealEntry[]>();
  for (const e of entradas) {
    const delDia = porDia.get(e.fecha);
    if (delDia === undefined) porDia.set(e.fecha, [e]);
    else delDia.push(e);
  }
  const dias: DiaDeEstimacion[] = [];
  for (let i = 0; i < DIAS_VENTANA; i++) {
    const fecha = sumarDias(desde, i);
    const delDia = porDia.get(fecha) ?? [];
    dias.push({
      fecha,
      kcal: sumar(delDia).kcal,
      conRegistros: delDia.length > 0,
      incompleto: incompletos.has(fecha),
    });
  }

  // Los pesajes dañados no cuentan, como en Cuerpo y en Hoy
  const legibles = pesajes.filter(pesajeLegible).filter((p) => p.fecha <= hoy);
  const pesos = pesosPorDia(legibles, ajustes.basculaTendenciaId);
  const resumen = resumenDePeso(legibles, ajustes.basculaTendenciaId, hoy);
  const datos: DatosDeEstimacion = {
    dias,
    tendenciaInicioKg: tendenciaDe(pesos, desde),
    tendenciaFinKg: tendenciaDe(pesos, hasta),
    pesoActualKg: resumen.tendenciaKg ?? resumen.ultimo?.pesoKg ?? null,
  };
  return { resultado: estimarMantenimiento(datos), dias, desde, hasta };
}

/**
 * Con la misma fecha, lo capturado después es lo que cuenta: este registro
 * queda estrictamente después de los que ya había, aunque se guarde en el
 * mismo milisegundo.
 */
async function creadoEnPara(desde: FechaLocal, existente: Maintenance | undefined): Promise<string> {
  if (existente !== undefined) return existente.creadoEn;
  let creadoEn = ahoraIso();
  const mismaFecha = await db.maintenance.where('desde').equals(desde).toArray();
  const ultimo = mismaFecha.reduce((max, r) => (r.creadoEn > max ? r.creadoEn : max), '');
  if (ultimo !== '' && creadoEn <= ultimo) creadoEn = new Date(Date.parse(ultimo) + 1).toISOString();
  return creadoEn;
}

export interface DecisionDePropuesta {
  /** Creado al abrir la pantalla: dos toques no duplican. */
  id: string;
  origen: 'formula' | 'estimado';
  propuestaKcal: number;
  /** Lo que se acepta o se modifica; null al rechazarla. */
  kcal: number | null;
  /** Los macros que van con esas kcal (0.5.1); null si no se aceptan macros. */
  macros: MacrosDiarios | null;
  decision: 'aceptada' | 'modificada' | 'rechazada';
  supuestos: string[];
  detalle: string;
}

const GRAMOS_MAX = 1000;

/** Los gramos de cada macro, o por qué no sirven. */
function revisarMacros(macros: MacrosDiarios | null, kcal: number): MacrosDiarios | null {
  if (macros === null) return null;
  for (const [campo, g] of [
    ['proteína', macros.proteinaG],
    ['carbohidratos', macros.carbohidratosG],
    ['grasa', macros.grasaG],
  ] as const) {
    if (!Number.isFinite(g) || g < 0 || g > GRAMOS_MAX) {
      throw new ErrorValidacion(`Los gramos de ${campo} deben estar entre 0 y ${GRAMOS_MAX}.`, campo);
    }
  }
  const limpios = {
    proteinaG: Math.round(macros.proteinaG),
    carbohidratosG: Math.round(macros.carbohidratosG),
    grasaG: Math.round(macros.grasaG),
  };
  if (!macrosCuadran(limpios, kcal)) {
    throw new ErrorValidacion(
      `Esos macros suman ${formatearKcal(kcalDeMacros(limpios))} y la meta es de ${formatearKcal(kcal)}: ajústalos para que cuadren.`,
      'macros',
    );
  }
  return limpios;
}

/**
 * Guarda la decisión sobre una propuesta (Req. 22.5): aceptada, modificada o
 * rechazada. Lo aceptado o modificado aplica desde hoy; los días anteriores
 * conservan el que tenían. Una rechazada se guarda sin kcal, para no volver a
 * ofrecer lo mismo. No se borra ni se reescribe nada. ESCRIBE.
 */
export async function guardarDecisionDePropuesta(d: DecisionDePropuesta, hoy: FechaLocal): Promise<Maintenance> {
  if (!esFechaLocal(hoy)) throw new ErrorValidacion('La fecha no es válida.');
  if (d.decision !== 'rechazada') {
    if (d.kcal === null || !Number.isFinite(d.kcal) || d.kcal < KCAL_MANTENIMIENTO_MIN || d.kcal > KCAL_MANTENIMIENTO_MAX) {
      throw new ErrorValidacion('El mantenimiento debe estar entre 800 y 6,000 kcal.', 'kcal');
    }
  }
  const kcalFinal = d.decision === 'rechazada' ? null : Math.round(d.kcal as number);
  const macros = kcalFinal === null ? null : revisarMacros(d.macros, kcalFinal);
  return enEscritura([db.maintenance], async () => {
    const existente = await db.maintenance.get(d.id);
    const registro: Maintenance = {
      id: d.id,
      desde: hoy,
      origen: d.origen,
      decision: d.decision,
      propuestaKcal: d.propuestaKcal,
      kcal: kcalFinal,
      macros,
      supuestos: [...d.supuestos],
      detalle: d.detalle,
      creadoEn: await creadoEnPara(hoy, existente),
    };
    await db.maintenance.put(registro);
    return registro;
  });
}

export const KCAL_MANTENIMIENTO_MIN = 800;
export const KCAL_MANTENIMIENTO_MAX = 6000;

export interface MantenimientoManual {
  /** Creado al abrir el formulario: guardar dos veces no duplica. */
  id: string;
  desde: FechaLocal;
  kcal: number;
  nota: string | null;
}

export async function guardarMantenimientoManual(
  datos: MantenimientoManual,
  hoy: FechaLocal,
): Promise<Maintenance> {
  if (!esFechaLocal(datos.desde)) throw new ErrorValidacion('La fecha no es válida.', 'desde');
  if (datos.desde < hoy) {
    throw new ErrorValidacion(
      'El mantenimiento aplica desde hoy o una fecha futura: los días anteriores conservan el que tenían.',
      'desde',
    );
  }
  if (
    !Number.isFinite(datos.kcal) ||
    datos.kcal < KCAL_MANTENIMIENTO_MIN ||
    datos.kcal > KCAL_MANTENIMIENTO_MAX
  ) {
    throw new ErrorValidacion('El mantenimiento debe estar entre 800 y 6,000 kcal.', 'kcal');
  }
  const nota = datos.nota?.trim() ?? '';
  return enEscritura([db.maintenance], async () => {
    const existente = await db.maintenance.get(datos.id);
    const creadoEn = await creadoEnPara(datos.desde, existente);
    const registro: Maintenance = {
      id: datos.id,
      desde: datos.desde,
      origen: 'manual',
      decision: 'manual',
      propuestaKcal: null,
      kcal: datos.kcal,
      supuestos: [],
      detalle: nota === '' ? 'Capturado a mano.' : `Capturado a mano. ${nota}`,
      creadoEn,
    };
    await db.maintenance.put(registro);
    return registro;
  });
}
